import { useMemo, useRef } from "react";
import { format, isSameDay, isToday } from "date-fns";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Event } from "@/hooks/useEvents";
import { DndContext, DragEndEvent, useSensor, useSensors, PointerSensor, useDroppable } from '@dnd-kit/core';
import { DraggableEventBlock } from "./DraggableEventBlock";
import { useDragEvent } from "@/hooks/useDragEvent";
import {
  generateTimeSlots,
  calculateEventPosition,
  detectOverlaps,
  getEventsForDay,
  formatHourLabel,
  HOUR_HEIGHT,
  DAY_START_HOUR,
  VISIBLE_HOURS,
} from "@/lib/calendarUtils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

interface DayViewProps {
  date: Date;
  events: Event[];
  onEventSelect: (event: Event) => void;
  onCreateEvent?: (startTime: Date, endTime: Date) => void;
  onUpdateEvent?: (id: string, data: Partial<Event>) => Promise<any>;
}

function DroppableTimeSlot({ day, hour, children }: { day: Date; hour: number; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `${day.toISOString()}-${hour}`,
    data: { day, hour },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "transition-colors",
        isOver && "bg-primary/10"
      )}
    >
      {children}
    </div>
  );
}

export function DayView({ date, events, onEventSelect, onCreateEvent, onUpdateEvent }: DayViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const timeSlots = useMemo(() => generateTimeSlots(), []);
  const dayEvents = useMemo(() => getEventsForDay(events, date), [events, date]);
  const eventsWithColumns = useMemo(() => detectOverlaps(dayEvents), [dayEvents]);

  const isDayToday = isToday(date);

  const sensors = useSensors(useSensor(PointerSensor));
  const { handleDragStart, handleDragEnd, handleDragCancel } = useDragEvent(
    events,
    onUpdateEvent || (async () => {})
  );

  const handleSlotClick = (hour: number) => {
    if (!onCreateEvent) return;
    
    const startTime = new Date(date);
    startTime.setHours(hour, 0, 0, 0);
    
    const endTime = new Date(startTime);
    endTime.setHours(hour + 1, 0, 0, 0);
    
    onCreateEvent(startTime, endTime);
  };

  // Calculate current time indicator position
  const currentTimeTop = useMemo(() => {
    if (!isDayToday) return null;
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    if (currentHour < DAY_START_HOUR || currentHour >= DAY_START_HOUR + VISIBLE_HOURS) {
      return null;
    }
    
    return ((currentHour - DAY_START_HOUR) * HOUR_HEIGHT) + (currentMinutes / 60 * HOUR_HEIGHT);
  }, [isDayToday]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="border rounded-lg bg-card overflow-hidden calendar-view">
        {/* Header */}
      <div className="border-b bg-muted/50 p-4">
        <h3 className={cn(
          "text-lg font-semibold",
          isDayToday && "text-primary"
        )}>
          {format(date, "EEEE, MMMM d, yyyy")}
        </h3>
      </div>

      {/* Time Grid */}
      <ScrollArea className="h-[600px]">
        <div className="relative" ref={containerRef}>
          <div className="grid grid-cols-[60px_1fr] sm:grid-cols-[80px_1fr]">
            {/* Time Labels Column */}
            <div>
              {timeSlots.map((slot, index) => (
                <div
                  key={index}
                  className="h-[60px] border-b border-r flex items-start justify-end pr-2 pt-1 text-xs text-muted-foreground"
                >
                  {formatHourLabel(slot.getHours())}
                </div>
              ))}
            </div>

            {/* Events Column */}
            <div className="relative">
              {/* Time Slot Grid */}
              {timeSlots.map((slot, index) => (
                <DroppableTimeSlot key={index} day={date} hour={slot.getHours()}>
                  <div
                    className="h-[60px] border-b hover:bg-accent/50 cursor-pointer group relative time-slot"
                    onClick={() => handleSlotClick(slot.getHours())}
                  >
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="sm" className="h-8 sm:h-6 text-xs px-2">
                        <Plus className="h-4 w-4 sm:h-3 sm:w-3 mr-1" />
                        <span className="hidden sm:inline">Add Event</span>
                        <span className="sm:hidden">Add</span>
                      </Button>
                    </div>
                  </div>
                </DroppableTimeSlot>
              ))}

              {/* Current Time Indicator */}
              {currentTimeTop !== null && (
                <div
                  className="absolute left-0 right-0 border-t-2 border-red-500 z-10"
                  style={{ top: `${currentTimeTop}px` }}
                >
                  <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-red-500" />
                </div>
              )}

              {/* Events */}
              {eventsWithColumns.map((event) => {
                const { top, height } = calculateEventPosition(
                  new Date(event.start_time),
                  new Date(event.end_time),
                  date
                );
                
                const width = 100 / event.totalColumns;
                const left = event.column * width;

                return (
                  <DraggableEventBlock
                    key={event.id}
                    event={event}
                    top={top}
                    height={height}
                    width={width}
                    left={left}
                    onClick={onEventSelect}
                    viewType="day"
                  />
                );
              })}
            </div>
          </div>
        </div>
      </ScrollArea>
      </div>
    </DndContext>
  );
}
